import { useEffect, useMemo } from "react";
import { withNamespaces } from "react-i18next";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import axios from "axios";
import {
    HiChevronDoubleDown,
    HiChevronDoubleUp,
    HiMenuAlt4,
} from "react-icons/hi";
import { Col, Container, Row } from "reactstrap";
import KanbanBoard from "src/components/KanbanBoard";
import Navigator from "src/components/Navigator";
import {
    API_URL_GET_KANBAN_BOARD,
    CM_API_URL_GET_KANBAN_BOARD,
} from "src/common/constants";
import { isFeatureEnabledForActiveModule } from "src/helpers/module_helper";
import { taskStatuses, priority } from "src/pages/Reporting/TaskManager/constants";
import { handleAddEllipsis } from "src/utils/utils";
import FilteringComponent from "./components/FilteringComponent";

const subtitleBlockStyle = {
    fontSize: 12,
    borderLeft: "4px solid #eee",
    paddingLeft: 20,
    color: "#999",
};

const WbCmKanbanBoard = ({ t, token, modules }) => {
    useEffect(() => {
        document.title = "Kanban Board | SIP";
    }, []);

    const statusesMap = useMemo(() => {
        return Object.fromEntries(
            taskStatuses.map((status) => [
                status.value,
                {
                    title: status.label,
                },
            ])
        );
    }, []);

    const prioritiesMap = useMemo(() => {
        const icons = {
            low: <HiChevronDoubleDown />,
            medium: <HiMenuAlt4 />,
            high: <HiChevronDoubleUp />,
        };

        return Object.fromEntries(
            priority.map((item) => [
                item.value,
                {
                    title: item.baseLabel,
                    icon: icons[item.value],
                },
            ])
        );
    }, []);

    const activeModule = modules?.active;

    const fetchBoardData = async (filters) => {
        const result = await axios.get(
            activeModule === "wb" ? API_URL_GET_KANBAN_BOARD : CM_API_URL_GET_KANBAN_BOARD,
            {
                params: filters,
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        );

        return result?.data?.data || [];
    };

    const getTaskLink = (data) => {
        return {
            pathname: `/admin/report/task/${data?.id}`,
            state: "desiredState",
        };
    };

    const onTaskLinkClick = (data) => {
        if (data?.primaryCaseId) {
            localStorage.setItem("caseId", data.primaryCaseId);
        }
    };

    const renderTaskId = (data) => {
        return `#T${data?.secondaryId || ""}`;
    };

    const renderSubtitle = (data, translate) => {
        return (
            <div className="mb-4" style={subtitleBlockStyle}>
                <p className="mb-1">
                    {translate("Case ID")}: {data?.caseId || ""}
                </p>
                <p className="mb-1">
                    {translate("Case Title")}: {handleAddEllipsis(data?.caseTitle)}
                </p>
            </div>
        );
    };

    if (!isFeatureEnabledForActiveModule("kanban-board")) {
        return (
            <div className="page-content">
                {t("Please upgrade your plan to access this feature")}.
            </div>
        );
    }

    return (
        <div className="page-content">
            <Container fluid style={{ minHeight: "500px" }}>
                <Row>
                    <Col lg="12" className="mb-4">
                        <Navigator
                            pageTitle="Kanban Board"
                            breadCrumbs={[
                                { title: "SIP", link: "/" },
                                { title: "Kanban Board", link: "#" },
                            ]}
                        />
                    </Col>
                </Row>

                <KanbanBoard
                    boardWrapperClassName="d-flex"
                    queryKey={["app-fetch-kanboard-tasks-list-query", activeModule]}
                    fetchBoardData={fetchBoardData}
                    statuses={statusesMap}
                    priorities={prioritiesMap}
                    FilteringComponent={FilteringComponent}
                    getTaskLink={getTaskLink}
                    onTaskLinkClick={onTaskLinkClick}
                    renderTaskId={renderTaskId}
                    renderSubtitle={renderSubtitle}
                    showEmptyState
                />
            </Container>
        </div>
    );
};

const mapStatetoProps = (state) => {
    const App = state.App;
    const { token, user } = state.Login;
    const { Modules: modules } = state;

    return {
        App,
        user,
        token,
        modules,
    };
};

export default withNamespaces()(
    withRouter(connect(mapStatetoProps, {})(WbCmKanbanBoard))
);
